import { useEffect, useState, useRef } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import DeleteModal from '../../components/modals/DeleteModal';
import { Modal } from 'react-bootstrap';
import AddVCMAdminModal from '../../components/modals/AddEditVCMAdminModal';
import { formatPhoneWithCountryCode } from '../../utils/phoneFormatter';

import '../../styles/ViewVCMAdmins.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ViewAdmins() {
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [vcm_admins, setVcmAdmins] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [notify, setNotify] = useState(null); 
    const notifyTimerRef = useRef(null);
    const [editingAdminData, setEditingAdminData] = useState(null);
    const [editingAdminId, setEditingAdminId] = useState(null);
    const [deleteModalInfo, setDeleteModalInfo] = useState({ id: null, confirmMessage: '' });

    // Handler to open delete modal with custom message
    const handleOpenDeleteModal = (vcm_admin) => {
        const confirmMessage = `Are you sure you want to delete VCM Admin "${vcm_admin.name}"? This action cannot be undone.`;
        setDeleteModalInfo({ id: vcm_admin.user_id, confirmMessage });
    };

  // Fetch admins from server
    const fetchVCMAdmins = async () => {
        setLoading(true);
        try {
        const response = await axios.get(`${BASE_URL}/api/admin/vcm-auth/get-all-vcm-admins`);
        if (response.status === 200 && response.data.success) {
            setVcmAdmins(response.data.vcm_admins || []);
            setErrorMsg('');
        } else {
            setErrorMsg(response.data.message || 'Failed to fetch VCM admin list');
        }
        } catch (error) {
        setErrorMsg('Server error. Please try again later.');
        console.error('Error fetching VCM admin list:', error);
        } finally {
        setLoading(false);
        }
    };

    // Handler for delete success
    const handleDeleteSuccess = () => {
      setDeleteModalInfo({ id: null, confirmMessage: '' });
      fetchVCMAdmins();
    };

  useEffect(() => {
    fetchVCMAdmins();
  }, []);

  useEffect(() => {
    return () => {
      try { if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current); } catch (e) {}
    };
  }, []);

  return (
    <div className="vcm-admin-container mt-4">
      <div className="vcm-admin-header">
        <h1>VCM Admins</h1>
        <button
          className="btn-custom-primary add-vcm-admin-btn"
          onClick={() => setShowAddModal(true)}
        >
          <FaPlus size={14} style={{ marginRight: "8px" }} />
          Add VCM Admins
        </button>
      </div>

      {errorMsg && (
        <Alert variant="danger" className="mt-3">
          {errorMsg}
        </Alert>
      )}

      {notify && (
        <Alert
          variant={notify.variant || "info"}
          onClose={() => setNotify(null)}
          dismissible
          className="mt-3"
        >
          {notify.message}
          {notify.token && (
            <div className="mt-2">
              <a
                className="btn btn-sm btn-primary"
                href={`/verify-vcm-admin?token=${encodeURIComponent(
                  notify.token
                )}`}
              >
                Open verification
              </a>
            </div>
          )}
        </Alert>
      )}

      {!loading && !errorMsg && (
        <div className="table-responsive">
          <Table className="align-middle vcm-admin-table" hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vcm_admins.map((admin_users, index) => (
                <tr key={admin_users.id || index}>
                  <td>{index + 1}</td>
                  <td>{admin_users.name}</td>
                  <td>{admin_users.email}</td>
                  <td>
                    {formatPhoneWithCountryCode(
                      admin_users.country_code,
                      admin_users.phone_number
                    )}
                  </td>
                  <td>
                    <FaEdit
                      className="action-icon me-2"
                      onClick={async () => {
                        const id = admin_users.user_id || admin_users.id;
                        try {
                          setErrorMsg("");
                          const res = await axios.get(
                            `${BASE_URL}/api/admin/vcm-auth/get-vcm-admin/${id}`
                          );
                          if (res.status === 200 && res.data.success) {
                            const adminData =
                              res.data.admin ||
                              res.data.vcm_admin ||
                              res.data.data ||
                              res.data;
                            setEditingAdminData(adminData || null);
                            setEditingAdminId(id);
                            setShowAddModal(true);
                          } else {
                            setErrorMsg(
                              res.data.message ||
                                "Failed to fetch admin details"
                            );
                          }
                        } catch (err) {
                          console.error("Error fetching admin details:", err);
                          setErrorMsg(
                            "Error fetching admin details. Please try again."
                          );
                        }
                      }}
                      title="Edit VCM Admin"
                      style={{ color: "#007bff", cursor: "pointer" }}
                    />
                    <FaTrash
                      className="action-icon text-danger"
                      onClick={() => handleOpenDeleteModal(admin_users)}
                      title="Delete"
                      style={{ cursor: "pointer" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {/* Delete Modal */}
      <DeleteModal
        id={deleteModalInfo.id}
        show={!!deleteModalInfo.id}
        onHide={() => setDeleteModalInfo({ id: null, confirmMessage: "" })}
        deleteUrl={(id) =>
          id ? `/api/admin/vcm-auth/delete-vcm-admin/${id}` : ""
        }
        entityLabel="VCM Admin"
        confirmMessage={deleteModalInfo.confirmMessage}
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* Add VCM Admin Modal */}
      {showAddModal && (
        <Modal
          show={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setEditingAdminData(null);
            setEditingAdminId(null);
          }}
          dialogClassName="vcm-modal-dialog"
          centered
        >
          <Modal.Header className="vcm-modal-header" closeButton>
            <Modal.Title>
              {editingAdminId ? "Update VCM Admin" : "Add VCM Admin"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddVCMAdminModal
              editData={editingAdminData}
              editId={editingAdminId}
              onSuccess={(payload) => {
                if (payload && payload.message) {
                  setNotify({
                    variant: "success",
                    message: payload.message,
                    token: payload.token,
                    verifyUrl: payload.verifyUrl,
                  });
                  // auto-dismiss after 3 seconds
                  try {
                    if (notifyTimerRef.current)
                      clearTimeout(notifyTimerRef.current);
                  } catch (e) {}
                  notifyTimerRef.current = setTimeout(
                    () => setNotify(null),
                    3000
                  );
                } else {
                  setNotify({
                    variant: "success",
                    message: "VCM admin saved successfully.",
                  });
                }
                setShowAddModal(false);
                setEditingAdminData(null);
                setEditingAdminId(null);
                fetchVCMAdmins();
              }}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}
